import React, { useState, useEffect, useRef } from 'react';
import { CheckCircle, Upload, X, Search } from 'lucide-react';
import CustomSelect from '../../../components/ui/CustomSelect';
import debtorApi, { Debtor } from '../../../api/debtorApi';

interface Step3Props {
  formData: any;
  handleChange: (field: string, value: any) => void;
  onDebtorSearch: (idNumber: string) => void;
  errors?: Record<string, string>;
}

export default function Step3DebtorData({
  formData,
  handleChange,
  onDebtorSearch,
  errors = {},
}: Step3Props) {
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Debtor[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const searchContainerRef = useRef<HTMLDivElement>(null);

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setIsLoading(true);
      const reader = new FileReader();
      reader.onloadend = () => {
        setTimeout(() => {
          setPhotoPreview(reader.result as string);
          setIsLoading(false);
        }, 300);
      };
      reader.readAsDataURL(file);
      handleChange('photo_file', file);
    }
  };

  const removePhoto = () => {
    setIsRemoving(true);
    setTimeout(() => {
      setPhotoPreview(null);
      setIsRemoving(false);
      handleChange('photo_file', null);
      handleChange('photo_url', '');
    }, 300);
  };

  // Search debitur berdasarkan NIK dengan debounce
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (searchQuery.trim().length >= 3) {
      searchTimeoutRef.current = setTimeout(async () => {
        setIsSearching(true);
        try {
          const results = await debtorApi.getAll({ search: searchQuery.trim() });
          // Filter hasil yang sesuai dengan NIK yang diketik
          const filteredResults = results.filter(
            debtor => debtor.id_number && debtor.id_number.includes(searchQuery.trim())
          );
          setSearchResults(filteredResults.slice(0, 5)); // Limit 5 results
          setShowSuggestions(filteredResults.length > 0);
        } catch (error) {
          console.error('Error searching debtors:', error);
          setSearchResults([]);
          setShowSuggestions(false);
        } finally {
          setIsSearching(false);
        }
      }, 300);
    } else {
      setSearchResults([]);
      setShowSuggestions(false);
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    if (value.trim().length === 0) {
      setShowSuggestions(false);
      setSearchResults([]);
    }
  };

  const handleSelectDebtor = (debtor: Debtor) => {
    handleChange('debtor_id', debtor.id);
    handleChange('debtor_name', debtor.full_name);
    handleChange('id_number', debtor.id_number);
    handleChange('birth_place', debtor.birth_place || '');
    handleChange('birth_date', debtor.birth_date?.split('T')[0] || '');
    handleChange('gender', debtor.gender || '');
    handleChange('marital_status', debtor.marital_status || '');
    handleChange('education', debtor.education || '');
    handleChange('phone', debtor.phone || '');
    handleChange('mother_maiden_name', debtor.mother_maiden_name || '');
    handleChange(
      'emergency_contact',
      debtor.emergency_contact_name && debtor.emergency_contact_phone
        ? `${debtor.emergency_contact_name} - ${debtor.emergency_contact_phone}`
        : ''
    );
    handleChange('photo_url', debtor.photo_url || '');
    if (debtor.photo_url) {
      setPhotoPreview(debtor.photo_url);
    }

    setSearchQuery('');
    setShowSuggestions(false);
    setSearchResults([]);
    onDebtorSearch(debtor.id_number);
  };

  return (
    <div className="space-y-4">
      {/* Auto-fill Section */}
      <div className="p-4 bg-blue-50 rounded-lg space-y-3">
        <label className="block text-sm font-medium text-gray-700">
          Cari Data Debitur (Auto-fill)
        </label>
        <div className="relative" ref={searchContainerRef}>
          <div className="flex gap-2 relative">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Masukkan No. KTP untuk mencari data..."
                value={searchQuery}
                onChange={e => handleSearchChange(e.target.value)}
                onFocus={() => {
                  if (searchResults.length > 0) {
                    setShowSuggestions(true);
                  }
                }}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 transition-colors duration-200"
              />
              {isSearching && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={() => {
                if (formData.id_number) {
                  onDebtorSearch(formData.id_number);
                }
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
            >
              Cari
            </button>
          </div>

          {/* Suggestions Dropdown */}
          {showSuggestions && searchResults.length > 0 && (
            <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
              {searchResults.map(debtor => (
                <div
                  key={debtor.id}
                  onClick={() => handleSelectDebtor(debtor)}
                  className="px-4 py-3 hover:bg-blue-50 cursor-pointer transition-colors duration-150 border-b border-gray-100 last:border-b-0"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{debtor.full_name}</p>
                      <p className="text-sm text-gray-500">NIK: {debtor.id_number}</p>
                      {debtor.birth_place && (
                        <p className="text-xs text-gray-400">{debtor.birth_place}</p>
                      )}
                    </div>
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 ml-2" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        {formData.debtor_id && (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded text-sm">
            <CheckCircle className="w-3 h-3" /> Data ditemukan di Arsip Debitur
          </span>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Nama Debitur <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.debtor_name}
            onChange={e => handleChange('debtor_name', e.target.value)}
            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 transition-colors duration-200 ${
              errors.debtor_name ? 'border-red-500' : 'border-gray-300'
            }`}
            required
          />
          {errors.debtor_name && <p className="text-sm text-red-600 mt-1">{errors.debtor_name}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Nomor KTP <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.id_number}
            onChange={e => handleChange('id_number', e.target.value)}
            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 transition-colors duration-200 ${
              errors.id_number ? 'border-red-500' : 'border-gray-300'
            }`}
            required
          />
          {errors.id_number && <p className="text-sm text-red-600 mt-1">{errors.id_number}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Tempat Lahir</label>
          <input
            type="text"
            value={formData.birth_place}
            onChange={e => handleChange('birth_place', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 transition-colors duration-200"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Tanggal Lahir</label>
          <input
            type="date"
            value={formData.birth_date}
            onChange={e => handleChange('birth_date', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 transition-colors duration-200"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Jenis Kelamin</label>
          <CustomSelect
            value={formData.gender}
            onChange={v => handleChange('gender', v)}
            placeholder="Pilih"
            width="w-full"
            color="blue"
            options={[
              { value: 'male', label: 'Laki-laki' },
              { value: 'female', label: 'Perempuan' },
            ]}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Status Pernikahan</label>
          <CustomSelect
            value={formData.marital_status}
            onChange={v => handleChange('marital_status', v)}
            placeholder="Pilih"
            width="w-full"
            color="blue"
            options={[
              { value: 'single', label: 'Belum Menikah' },
              { value: 'married', label: 'Menikah' },
              { value: 'divorced', label: 'Cerai' },
            ]}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Pendidikan Terakhir
          </label>
          <input
            type="text"
            value={formData.education}
            onChange={e => handleChange('education', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 transition-colors duration-200"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Upload Foto</label>
          <div className="relative">
            <input
              type="file"
              accept="image/*"
              className="hidden"
              id="photo-upload"
              onChange={handlePhotoChange}
            />
            {isLoading ? (
              <div className="flex flex-col items-center justify-center gap-2 w-full px-4 py-8 border-2 border-dashed border-blue-300 rounded-lg bg-blue-50 animate-pulse">
                <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                <span className="text-sm text-blue-500">Memuat gambar...</span>
              </div>
            ) : photoPreview ? (
              <div
                className={`relative inline-block rounded-lg overflow-hidden border-2 border-gray-200 ${
                  isRemoving ? 'animate-imageOut' : 'animate-imageIn'
                }`}
              >
                <img
                  src={photoPreview}
                  alt="Preview"
                  className="max-w-full max-h-64 object-contain transition-transform duration-300 hover:scale-105"
                />
                <button
                  type="button"
                  onClick={removePhoto}
                  className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 hover:scale-110 transition-all duration-200 shadow-lg"
                >
                  <X className="w-4 h-4" />
                </button>
                <label
                  htmlFor="photo-upload"
                  className="absolute bottom-2 right-2 px-3 py-1.5 bg-blue-600 text-white text-xs rounded-lg cursor-pointer hover:bg-blue-700 hover:scale-105 transition-all duration-200 shadow-lg"
                >
                  Ganti Foto
                </label>
              </div>
            ) : (
              <label
                htmlFor="photo-upload"
                className="flex flex-col items-center justify-center gap-2 w-full px-4 py-8 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-500 hover:bg-blue-50 hover:scale-[1.01] transition-all duration-300 group"
              >
                <Upload className="w-8 h-8 text-gray-400 group-hover:text-blue-500 group-hover:scale-110 transition-all duration-300" />
                <span className="text-sm text-gray-500 group-hover:text-blue-600 transition-colors duration-300">
                  Pilih foto atau drag & drop
                </span>
              </label>
            )}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Nomor Telepon / WhatsApp
          </label>
          <input
            type="text"
            value={formData.phone}
            onChange={e => handleChange('phone', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 transition-colors duration-200"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Nama Gadis Ibu Kandung
          </label>
          <input
            type="text"
            value={formData.mother_maiden_name}
            onChange={e => handleChange('mother_maiden_name', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 transition-colors duration-200"
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">Kontak Darurat</label>
          <input
            type="text"
            value={formData.emergency_contact}
            onChange={e => handleChange('emergency_contact', e.target.value)}
            placeholder="Nama & No. Telepon"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 transition-colors duration-200"
          />
        </div>
      </div>
    </div>
  );
}
